import { backendUrl } from '@/config/backend';
import { getSchoolConfig, getStoredToken, resolveSchoolId } from '@/config/schools';
import { getMenuForDate as getGitHubMenuForDate } from '@/data/menuLoader';

const diningCache = new Map();

function getDiningCacheKey(date, schoolId) {
  return `${schoolId}:${date}`;
}

function buildEmptyDiningPayload(date) {
  return {
    date,
    has_menu: false,
    sections: [],
  };
}

function normalizeTextList(value) {
  if (Array.isArray(value)) {
    return value
      .map((entry) => String(entry || '').trim())
      .filter(Boolean);
  }

  if (typeof value === 'string') {
    return value
      .split(',')
      .map((entry) => entry.trim())
      .filter(Boolean);
  }

  return [];
}

function normalizeCalories(value) {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
}

function pluralizeServingUnit(amount, unit) {
  const normalizedUnit = String(unit || '').trim();
  if (!normalizedUnit) {
    return '';
  }

  if (Number(amount) === 1) {
    return normalizedUnit;
  }

  if (/serving$/i.test(normalizedUnit)) {
    return normalizedUnit.replace(/serving$/i, 'servings');
  }

  return normalizedUnit;
}

function formatServing(amount, unit) {
  const normalizedAmount = String(amount ?? '').trim();
  const normalizedUnit = pluralizeServingUnit(amount, unit);
  const serving = [normalizedAmount, normalizedUnit].filter(Boolean).join(' ').trim();
  return serving || null;
}

function normalizeServing(value, amount = null, unit = null) {
  if (typeof value === 'string') {
    const normalized = value.replace(/\s+/g, ' ').trim();
    if (normalized) {
      const match = normalized.match(/^(\d+(?:\.\d+)?)\s+serving$/i);
      if (match) {
        return `${match[1]} ${pluralizeServingUnit(match[1], 'serving')}`;
      }
      return normalized;
    }
  }

  if (value && typeof value === 'object') {
    return formatServing(
      value.serving_size_amount ?? value.amount,
      value.serving_size_unit ?? value.unit,
    );
  }

  return formatServing(amount, unit);
}

function normalizeStructuredMenuDay(dayData, date) {
  const sections = (dayData.sections || [])
    .map((section, sectionIndex) => {
      const items = (section.items || [])
        .map((item, itemIndex) => {
          const name = String(item.name || item.text || '').trim();
          if (!name) {
            return null;
          }

          return {
            id: item.id ?? `${sectionIndex}-${itemIndex}-${name}`,
            name,
            description: String(item.description || '').trim(),
            calories: normalizeCalories(item.calories),
            serving: normalizeServing(
              item.serving,
              item.serving_size_amount,
              item.serving_size_unit,
            ),
            allergens: normalizeTextList(item.allergens),
          };
        })
        .filter(Boolean);

      const name = String(section.name || section.title || '').trim();
      if (!name || !items.length) {
        return null;
      }

      return { name, items };
    })
    .filter(Boolean);

  return {
    date: dayData.date || date,
    has_menu: typeof dayData.has_menu === 'boolean' ? dayData.has_menu : sections.length > 0,
    sections,
  };
}

function getMenuSectionKey(menuItem) {
  if (menuItem?.station_id !== null && menuItem?.station_id !== undefined) {
    return `station:${menuItem.station_id}`;
  }

  if (menuItem?.menu_id !== null && menuItem?.menu_id !== undefined) {
    return `menu:${menuItem.menu_id}`;
  }

  return null;
}

function normalizeGitHubMenuDay(dayData, date) {
  if (!dayData) {
    return buildEmptyDiningPayload(date);
  }

  if (Array.isArray(dayData.sections)) {
    return normalizeStructuredMenuDay(dayData, date);
  }

  const sectionsByKey = new Map();
  for (const [index, menuItem] of (dayData.menu_items || []).entries()) {
    const sectionKey = getMenuSectionKey(menuItem);
    if (!sectionKey) {
      continue;
    }

    const existingSection = sectionsByKey.get(sectionKey) || {
      name: '',
      items: [],
      order: index,
      menuPosition: Number.MAX_SAFE_INTEGER,
    };

    if (menuItem.is_section_title && menuItem.is_station_header) {
      existingSection.name = menuItem.text || existingSection.name;
      existingSection.order = Math.min(existingSection.order, index);
      existingSection.menuPosition = Math.min(
        existingSection.menuPosition,
        dayData.menu_info?.[menuItem.menu_id]?.position ?? Number.MAX_SAFE_INTEGER,
      );
      sectionsByKey.set(sectionKey, existingSection);
      continue;
    }

    if (!menuItem.food) {
      sectionsByKey.set(sectionKey, existingSection);
      continue;
    }

    const food = menuItem.food || {};
    const nutrition = food.rounded_nutrition_info || {};
    const icons = food.icons?.food_icons || [];

    existingSection.items.push({
      id: food.id,
      name: food.name || menuItem.text || '',
      description: food.description || '',
      calories: normalizeCalories(nutrition.calories),
      serving: normalizeServing(food.serving_size_info),
      allergens: normalizeTextList(icons.map((icon) => icon.synced_name || icon.name)),
    });

    sectionsByKey.set(sectionKey, existingSection);
  }

  const sections = [...sectionsByKey.values()]
    .filter((section) => section.items.length > 0)
    .sort((left, right) => {
      if (left.menuPosition !== right.menuPosition) {
        return left.menuPosition - right.menuPosition;
      }

      return left.order - right.order;
    })
    .map(({ name, items }) => ({ name, items }));

  return {
    date,
    has_menu: sections.length > 0,
    sections,
  };
}

async function buildDiningError(response) {
  let message = `Failed to load dining menu. (HTTP ${response.status})`;

  try {
    const payload = await response.json();
    if (payload?.message) {
      message = payload.message;
    }
  } catch (error) {
    // Ignore non-JSON error bodies and keep the default message.
  }

  const requestError = new Error(message);
  requestError.status = response.status;
  return requestError;
}

async function getBackendDiningForDate(date, schoolId) {
  const requestUrl = new URL(backendUrl('/dining'));
  requestUrl.searchParams.set('date', date);
  requestUrl.searchParams.set('schoolId', schoolId);

  const token = getStoredToken();
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const response = await fetch(requestUrl.toString(), { headers });

  if (!response.ok) {
    throw await buildDiningError(response);
  }

  return response.json();
}

async function getGitHubDiningForDate(date, schoolId) {
  const rawMenuDay = await getGitHubMenuForDate(date, schoolId);
  return normalizeGitHubMenuDay(rawMenuDay, date);
}

export async function getDiningForDate(date, schoolId = null) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(date || ''))) {
    throw new Error('Invalid date. Use YYYY-MM-DD.');
  }

  const resolvedSchoolId = resolveSchoolId({ schoolId });
  const cacheKey = getDiningCacheKey(date, resolvedSchoolId);

  if (diningCache.has(cacheKey)) {
    return diningCache.get(cacheKey);
  }

  const school = getSchoolConfig(resolvedSchoolId);
  const dining = school.menuSource === 'github'
    ? await getGitHubDiningForDate(date, resolvedSchoolId)
    : await getBackendDiningForDate(date, resolvedSchoolId);

  diningCache.set(cacheKey, dining);
  return dining;
}

export function clearDiningCache() {
  diningCache.clear();
}
